/**
 * SENTINEL — SPA de búsqueda para investigación de siniestros (React 19 + React Router
 * + TanStack Query + Tailwind 4). Se sirve desde el mismo proceso Express que la API
 * (mismo origen: cookie de sesión HttpOnly SameSite=Strict, sin CORS ni tokens en JS).
 *
 * Mapa de páginas
 *   Públicas
 *     /login                     Ingreso (error genérico, cuenta regresiva ante 429).
 *     /set-password#t=<token>    Definir contraseña desde invitación o restablecimiento
 *                                (el token se lee del fragmento y se borra de la URL).
 *   Con sesión (AppShell: barra superior, navegación, menú de usuario)
 *     /buscar[?s=<searchId>]     Búsqueda unificada: contexto (finalidad, caso, rol),
 *                                caja única, filtros, resultados del índice propio y
 *                                panel "Internet en vivo". La URL solo lleva el id.
 *     /avisos/:id[?tab=&snap=]   Detalle del aviso: captura, fotos, datos, texto
 *                                indexado, JSON crudo, HTML e historial de capturas.
 *     /historial                 Búsquedas propias y de la institución (reabrir).
 *     /cuenta                    Cambio de contraseña y sesiones activas.
 *   Solo administradores (código separado, carga diferida)
 *     /admin/instituciones[/:id] Instituciones: alta, edición, suspensión, detalle.
 *     /admin/usuarios            Usuarios: invitación, restablecer, bloquear, sesiones.
 *     /admin/fuentes             Fuentes sociales (targets del scraper): alta, pausa, borrado.
 *     /admin/recoleccion         PCs recolectores y cuentas de redes sociales (control remoto).
 *     /admin/auditoria           Registro de auditoría con filtros y exportación CSV.
 *     /admin/uso                 Uso por institución, usuario o día.
 *     /admin/sistema             Estado de la recolección, OCR, alertas y proveedores.
 *   *                            404; las rutas de administración responden 403 a usuarios.
 *
 * Privacidad en el navegador: los datos viven solo en la memoria de React Query (se
 * borran al cerrar sesión o ante un 401); lo único persistido es el contexto de la
 * búsqueda, en sessionStorage de la pestaña. Nunca se pone texto buscado en la URL.
 */

import { StrictMode, type ComponentType } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, data, redirect, RouterProvider, type RouteObject } from "react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { isApiError, safeNext, setUnauthorizedHandler } from "./api.js";
import { AppShell } from "./components/AppShell.js";
import { Splash } from "./components/Brand.js";
import { ToastProvider } from "./components/ui/Toast.js";
import { AccountPage } from "./pages/AccountPage.js";
import { NotFoundPage, RouteErrorPage } from "./pages/ErrorPages.js";
import { HistoryPage } from "./pages/HistoryPage.js";
import { LoginPage } from "./pages/LoginPage.js";
import { SearchPage } from "./pages/search/SearchPage.js";
import { SetPasswordPage } from "./pages/SetPasswordPage.js";
import { createQueryClient, meQuery } from "./queries.js";
import "./styles.css";

const queryClient = createQueryClient();

/** Ruta actual (sin fragmento) para volver tras ingresar. */
function currentPath(url: URL | Location): string {
  return `${url.pathname}${url.search}`;
}

function loginRedirect(url: URL | Location) {
  const here = currentPath(url);
  return here === "/" || here === "/buscar" ? "/login" : `/login?next=${encodeURIComponent(here)}`;
}

/**
 * Puerta de la app: sin sesión válida no se pinta nada protegido. El resultado de
 * /api/auth/me queda en caché y AppShell lo lee sin volver a pedirlo.
 */
async function requireSession({ request }: { request: Request }) {
  try {
    return await queryClient.ensureQueryData(meQuery);
  } catch (err) {
    if (isApiError(err, 401)) throw redirect(loginRedirect(new URL(request.url)));
    throw err;
  }
}

/** Las rutas /admin no existen para un usuario normal: 403 explícito, no un menú oculto nada más. */
async function requireAdmin({ request }: { request: Request }) {
  const me = await requireSession({ request });
  if (me.user.role !== "admin") throw data("Solo administradores", { status: 403 });
  return null;
}

/** Si ya hay sesión, /login no tiene sentido: se va directo a la app. */
async function redirectIfLoggedIn({ request }: { request: Request }) {
  const cached = queryClient.getQueryData(meQuery.queryKey);
  if (cached) {
    throw redirect(safeNext(new URL(request.url).searchParams.get("next")));
  }
  return null;
}

const lazyPage = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) =>
  async () => ({ Component: (await load())[name] });

const adminRoutes: RouteObject[] = [
  { index: true, loader: () => redirect("/admin/instituciones") },
  {
    path: "instituciones",
    lazy: lazyPage(() => import("./pages/admin/InstitutionsPage.js"), "InstitutionsPage"),
  },
  {
    path: "instituciones/:id",
    lazy: lazyPage(() => import("./pages/admin/InstitutionDetailPage.js"), "InstitutionDetailPage"),
  },
  { path: "usuarios", lazy: lazyPage(() => import("./pages/admin/UsersPage.js"), "UsersPage") },
  { path: "fuentes", lazy: lazyPage(() => import("./pages/admin/TargetsPage.js"), "TargetsPage") },
  { path: "recoleccion", lazy: lazyPage(() => import("./pages/admin/SocialPage.js"), "SocialPage") },
  { path: "rendimiento", lazy: lazyPage(() => import("./pages/admin/SourceYieldPage.js"), "SourceYieldPage") },
  { path: "auditoria", lazy: lazyPage(() => import("./pages/admin/AuditPage.js"), "AuditPage") },
  { path: "uso", lazy: lazyPage(() => import("./pages/admin/UsagePage.js"), "UsagePage") },
  { path: "sistema", lazy: lazyPage(() => import("./pages/admin/SystemPage.js"), "SystemPage") },
];

const routes: RouteObject[] = [
  { path: "/login", loader: redirectIfLoggedIn, Component: LoginPage, ErrorBoundary: RouteErrorPage },
  { path: "/set-password", Component: SetPasswordPage, ErrorBoundary: RouteErrorPage },
  {
    path: "/",
    loader: requireSession,
    Component: AppShell,
    ErrorBoundary: RouteErrorPage,
    HydrateFallback: Splash,
    children: [
      {
        ErrorBoundary: RouteErrorPage,
        children: [
          { index: true, loader: () => redirect("/buscar") },
          { path: "buscar", Component: SearchPage },
          {
            path: "avisos/:id",
            lazy: lazyPage(() => import("./pages/listing/ListingPage.js"), "ListingPage"),
          },
          { path: "historial", Component: HistoryPage },
          { path: "cuenta", Component: AccountPage },
          {
            path: "admin",
            loader: requireAdmin,
            lazy: lazyPage(() => import("./pages/admin/AdminLayout.js"), "AdminLayout"),
            children: adminRoutes,
          },
          { path: "*", Component: NotFoundPage },
        ],
      },
    ],
  },
];

const router = createBrowserRouter(routes);

// Sesión vencida o revocada en cualquier llamada: se olvida todo lo cargado y se
// vuelve al ingreso recordando dónde estaba el analista. El contexto del caso
// (sessionStorage de la pestaña) se conserva: tras reingresar sigue el mismo caso;
// el cierre de sesión explícito sí lo borra.
setUnauthorizedHandler(() => {
  queryClient.clear();
  if (window.location.pathname === "/login") return;
  void router.navigate(loginRedirect(window.location), { replace: true });
});

const root = document.getElementById("root");
if (!root) throw new Error("Falta el elemento #root en index.html");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);

// PWA instalable: el service worker solo cachea el app shell (nunca /api/*). Se registra
// únicamente en producción (en dev Vite sirve módulos sin hash y el SW estorbaría) y
// envuelto en try/catch: si el registro falla, la app arranca igual.
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").catch((err) => {
      console.warn("No se pudo registrar el service worker", err);
    });
  });
}
